// POAListPage.tsx  — /poa
// Shows all Form 2848 POAs with status and signature progress.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle2, Clock, Send, AlertCircle, Copy, Trash2, PenLine, BellRing } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import CopyPOAModal from './CopyPOAModal';
import { REMINDERS_ENABLED } from './poaConstants';
import { voidPOA, sendToClient, remindClient } from '../../services/poa';

interface POARow {
  id:               string;
  status:           string;
  taxpayerName:     string;
  taxpayerEin:      string | null;
  taxpayerSsn:      string | null;
  peClientCode:     string | null;
  signedDocTitle:   string | null;
  taxYearEarliest:  number | null;
  taxYearLatest:    number | null;
  createdBy:        string;
  createdAt:        string;
  taxpayerEmail:    string | null;
  sentToClientAt:   string | null;
  lastReminderAt:   string | null;
  reminderCount:    number;
  repTotal:         number;
  repSigned:        number;
  pendingSignerNames: string | null;
  currentUserCanSign:  boolean;
  currentUserIsSigner: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  draft:                { label: 'Draft',                    color: '#9aa5ce',       icon: <Clock size={13} /> },
  pending_internal_sig: { label: 'Awaiting Internal Sigs',   color: '#ff9e64',       icon: <Clock size={13} /> },
  pending_client_sig:   { label: 'To Be Sent for Client Sig', color: '#7aa2f7',      icon: <Send size={13} /> },
  sent_to_client:       { label: 'Awaiting Client Signature', color: '#bb9af7',      icon: <Send size={13} /> },
  complete:             { label: 'Complete',                 color: '#9ece6a',        icon: <CheckCircle2 size={13} /> },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: 'var(--muted)', icon: null };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}40`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole days since an ISO timestamp, or null when there isn't one. */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** A POA sitting with a client goes amber at a week and red at a fortnight. */
function agingColor(days: number | null): string {
  if (days === null) return 'var(--muted)';
  if (days >= 14) return '#f7768e';
  if (days >= 7)  return '#ff9e64';
  return 'var(--muted)';
}

function agingLabel(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export default function POAListPage() {
  const navigate    = useNavigate();
  const { email }   = useAuth();
  const userEmail   = email?.toLowerCase() ?? '';

  const [rows,    setRows]    = useState<POARow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [filter,  setFilter]  = useState<'all' | 'mine' | 'pending' | 'awaiting'>('pending');
  const [copying,  setCopying]  = useState<{ id: string; name: string } | null>(null);
  const [voiding,  setVoiding]  = useState<string | null>(null);
  const [sending,  setSending]  = useState<string | null>(null); // poaId being sent
  const [reminding, setReminding] = useState<string | null>(null); // poaId being nudged

  useEffect(() => {
    fetch('/api/poa-list', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Error ${r.status}`);
        return r.json();
      })
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleVoid(row: POARow, e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(`Void POA for ${row.taxpayerName}? This cannot be undone.`)) return;
    setVoiding(row.id);
    try {
      await voidPOA(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err: any) {
      alert(`Could not void: ${err.message}`);
    } finally {
      setVoiding(null);
    }
  }

  async function handleSend(row: POARow, e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(`Send Form 2848 for ${row.taxpayerName} via SafeSend?`)) return;
    setSending(row.id);
    try {
      await sendToClient(row.id);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'sent_to_client' } : r));
    } catch (err: any) {
      alert(`Could not send: ${err.message}`);
    } finally {
      setSending(null);
    }
  }

  async function handleRemind(row: POARow, e: React.MouseEvent) {
    e.stopPropagation();
    const waited = daysSince(row.sentToClientAt);
    const waitedLabel = waited === null ? '' : ` It was sent ${agingLabel(waited)}.`;
    if (!window.confirm(
      `Send a signature reminder to ${row.taxpayerEmail ?? 'the taxpayer'} for ${row.taxpayerName}?${waitedLabel}`
    )) return;
    setReminding(row.id);
    try {
      const result = await remindClient(row.id);
      setRows((prev) => prev.map((r) => r.id === row.id
        ? { ...r, lastReminderAt: result.lastReminderAt, reminderCount: result.reminderCount }
        : r));
      if (!result.hadSigningUrl) {
        alert('Reminder sent. We had no stored signing link for this POA, so the email points the client back to their original SafeSend message.');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setReminding(null);
    }
  }

  const isPending = (r: POARow) =>
    r.status === 'draft' || r.status === 'pending_internal_sig' || r.status === 'pending_client_sig';

  const isAwaitingClient = (r: POARow) => r.status === 'sent_to_client';

  const filtered = rows.filter((r) => {
    if (filter === 'pending')  return isPending(r);
    if (filter === 'awaiting') return isAwaitingClient(r);
    if (filter === 'mine')     return r.createdBy?.toLowerCase() === userEmail;
    return true;
  });

  const pendingCount  = rows.filter(isPending).length;
  const awaitingCount = rows.filter(isAwaitingClient).length;
  // Anything out with a client for two weeks or more, regardless of who created it.
  const staleCount = rows.filter(
    (r) => isAwaitingClient(r) && (daysSince(r.sentToClientAt) ?? 0) >= 14,
  ).length;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Form 2848 — Power of Attorney</h2>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            {rows.length} total &ensp;·&ensp;
            <span style={{ color: pendingCount > 0 ? 'var(--warn)' : 'var(--muted)' }}>
              {pendingCount} action required
            </span>
            {staleCount > 0 && (
              <>
                &ensp;·&ensp;
                <span style={{ color: '#f7768e' }}>
                  {staleCount} waiting on a client 14+ days
                </span>
              </>
            )}
          </div>
        </div>
        <button
          className="btn"
          style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(122,162,255,.12)', borderColor: 'rgba(122,162,255,.4)', color: 'var(--accent)' }}
          onClick={() => navigate('/poa/new')}
        >
          <Plus size={14} /> New Form 2848
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,.04)', borderRadius: 8, padding: 3, width: 'fit-content' }}>
        {([
          { key: 'pending',  label: `Pending (${pendingCount})` },
          { key: 'awaiting', label: `Awaiting client (${awaitingCount})` },
          { key: 'mine',     label: 'Created by me' },
          { key: 'all',      label: 'All' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 13, fontFamily: 'inherit',
              background: filter === key ? 'rgba(122,162,255,.18)' : 'transparent',
              color: filter === key ? 'var(--accent)' : 'var(--muted)',
              fontWeight: filter === key ? 600 : 400,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{ color: 'var(--bad)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16 }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>Loading…</div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
          {filter === 'pending'  ? 'No POAs currently require action.'
            : filter === 'awaiting' ? 'Nothing is waiting on a client signature.'
            : 'No POAs found.'}
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Taxpayer', 'Tax Year(s)', 'Status', 'Signatures', 'Created', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11,
                    fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const yearLabel = row.taxYearEarliest === row.taxYearLatest
                  ? String(row.taxYearEarliest ?? '—')
                  : `${row.taxYearEarliest}–${row.taxYearLatest}`;
                const tin = row.taxpayerEin ?? row.taxpayerSsn ?? '—';

                return (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/poa/${row.id}`)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background .1s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{row.taxpayerName}</div>
                      {row.signedDocTitle && (
                        <div title={row.signedDocTitle} style={{ fontSize: 12, marginTop: 2, color: 'var(--accent, #7aa2f7)' }}>
                          {row.signedDocTitle}
                        </div>
                      )}
                      <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
                        {tin}
                        {row.peClientCode && (
                          <span style={{ fontFamily: 'monospace', marginLeft: 8, opacity: 0.7 }}>{row.peClientCode}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)' }}>{yearLabel}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatusBadge status={row.status} />
                      {row.status === 'sent_to_client' && row.sentToClientAt && (
                        <div style={{ fontSize: 11, marginTop: 4, color: agingColor(daysSince(row.sentToClientAt)), whiteSpace: 'nowrap' }}>
                          Sent {fmtDate(row.sentToClientAt)} · {agingLabel(daysSince(row.sentToClientAt))}
                        </div>
                      )}
                      {row.status === 'sent_to_client' && row.reminderCount > 0 && (
                        <div style={{ fontSize: 11, marginTop: 2, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {row.reminderCount} reminder{row.reminderCount === 1 ? '' : 's'}
                          {row.lastReminderAt && ` · last ${agingLabel(daysSince(row.lastReminderAt))}`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {row.repSigned === row.repTotal
                          ? <CheckCircle2 size={14} color="var(--ok)" />
                          : <Clock size={14} color="var(--muted)" />}
                        <span style={{ color: row.repSigned === row.repTotal ? 'var(--ok)' : 'var(--text)' }}>
                          {row.repSigned}/{row.repTotal}
                        </span>
                      </div>
                      {row.pendingSignerNames && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                          Pending: {row.pendingSignerNames}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                      <div>{fmtDate(row.createdAt)}</div>
                      <div style={{ fontSize: 11, marginTop: 2 }}>{row.createdBy}</div>
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                        {row.currentUserCanSign && (
                          <button
                            className="btn"
                            style={{
                              fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
                              color: '#9ece6a', borderColor: 'rgba(158,206,106,.35)',
                              background: 'rgba(158,206,106,.07)',
                            }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/poa/${row.id}`); }}
                          >
                            <PenLine size={11} /> Sign Now
                          </button>
                        )}
                        {row.status === 'pending_client_sig' && row.repSigned === row.repTotal &&
                         (row.createdBy?.toLowerCase() === userEmail || row.currentUserIsSigner) && (
                          <button
                            className="btn"
                            disabled={sending === row.id}
                            style={{
                              fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
                              color: '#7aa2f7', borderColor: 'rgba(122,162,247,.35)',
                              background: 'rgba(122,162,247,.07)',
                              opacity: sending === row.id ? 0.5 : 1,
                            }}
                            onClick={(e) => handleSend(row, e)}
                          >
                            <Send size={11} /> {sending === row.id ? '…' : 'Send via SafeSend'}
                          </button>
                        )}
                        {REMINDERS_ENABLED && row.status === 'sent_to_client' && row.taxpayerEmail && (
                          <button
                            className="btn"
                            disabled={reminding === row.id}
                            title={`Remind ${row.taxpayerEmail}`}
                            style={{
                              fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
                              color: '#bb9af7', borderColor: 'rgba(187,154,247,.35)',
                              background: 'rgba(187,154,247,.07)',
                              opacity: reminding === row.id ? 0.5 : 1,
                            }}
                            onClick={(e) => handleRemind(row, e)}
                          >
                            <BellRing size={11} /> {reminding === row.id ? '…' : 'Remind'}
                          </button>
                        )}
                        {row.createdBy?.toLowerCase() === userEmail && row.status !== 'complete' && (
                          <button
                            className="btn"
                            disabled={voiding === row.id}
                            style={{
                              fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4,
                              color: 'var(--bad)', borderColor: 'rgba(247,118,142,.35)',
                              background: 'rgba(247,118,142,.07)',
                              opacity: voiding === row.id ? 0.5 : 1,
                            }}
                            onClick={(e) => handleVoid(row, e)}
                          >
                            <Trash2 size={11} /> {voiding === row.id ? '…' : 'Void'}
                          </button>
                        )}
                        <button
                          className="btn"
                          style={{ fontSize: 12, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                          onClick={(e) => { e.stopPropagation(); setCopying({ id: row.id, name: row.taxpayerName }); }}
                        >
                          <Copy size={11} /> Copy
                        </button>
                        <span style={{ fontSize: 12, color: 'var(--accent)' }}>View →</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {copying && (
        <CopyPOAModal
          poaId={copying.id}
          taxpayerName={copying.name}
          onClose={() => setCopying(null)}
        />
      )}
    </div>
  );
}
